import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Crown, Eye, EyeOff, Gift, AlertTriangle } from 'lucide-react';

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  
  const redirectUrl = searchParams.get('redirect');

  useEffect(() => {
    // Get referral code from URL if present
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref.toUpperCase());
    }
  }, [searchParams]);

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!dateOfBirth) {
      toast.error('Please enter your date of birth');
      return;
    }

    const age = calculateAge(dateOfBirth);
    if (age < 21) {
      toast.error('You must be at least 21 years old to join');
      return;
    }

    if (!ageConfirmed) {
      toast.error('Please confirm you are 21 years or older');
      return;
    }
    
    setLoading(true);
    
    try {
      await register(email, password, name, dateOfBirth, referralCode);
      toast.success('Account created successfully!');
      navigate(redirectUrl || '/pricing');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Calculate max date (21 years ago)
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - 21);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 py-12">
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1550053808-52a75a05955d?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      <div className="absolute inset-0 bg-[#050505]/90" />
      
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <Crown className="w-10 h-10 text-gold-400" />
            <span className="font-heading text-2xl font-bold text-white">House of Royalty</span>
          </Link>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-2">Join the Royalty</h1>
          <p className="text-zinc-500">Create your account to begin your journey</p>
        </div>

        {/* Age Restriction Notice */}
        <div className="flex items-center gap-3 p-4 bg-amber-500/10 border border-amber-500/30 mb-6">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <p className="text-amber-400 text-sm">
            <strong>21+ Only.</strong> You must be at least 21 years old to join House of Royalty.
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="glass-heavy p-8 space-y-5">
          {referralCode && (
            <div className="flex items-center gap-3 p-3 bg-gold-500/10 border border-gold-500/30">
              <Gift className="w-5 h-5 text-gold-400" />
              <p className="text-gold-400 text-sm">You were referred! Code: <span className="font-mono font-bold">{referralCode}</span></p>
            </div>
          )}
          
          <div>
            <label className="block text-sm text-zinc-400 mb-2 uppercase tracking-wider">Full Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-royal w-full"
              placeholder="Your full name"
              required
              data-testid="register-name"
            />
          </div>
          
          <div>
            <label className="block text-sm text-zinc-400 mb-2 uppercase tracking-wider">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-royal w-full"
              placeholder="your@email.com"
              required
              data-testid="register-email"
            />
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-2 uppercase tracking-wider">Date of Birth</label>
            <input
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              className="input-royal w-full"
              max={maxDateStr}
              required
              data-testid="register-dob"
            />
            <p className="text-zinc-600 text-xs mt-1">Must be 21 years or older</p>
          </div>
          
          <div>
            <label className="block text-sm text-zinc-400 mb-2 uppercase tracking-wider">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-royal w-full pr-12"
                placeholder="Min. 6 characters"
                required
                minLength={6}
                data-testid="register-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          
          {!referralCode && (
            <div>
              <label className="block text-sm text-zinc-400 mb-2 uppercase tracking-wider">
                Referral Code <span className="text-zinc-600">(optional)</span>
              </label>
              <input
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                className="input-royal w-full font-mono"
                placeholder="Enter referral code"
                data-testid="register-referral"
              />
            </div>
          )}

          {/* Age Confirmation Checkbox */}
          <div className="flex items-start gap-3 py-2">
            <input
              type="checkbox"
              id="age-confirm"
              checked={ageConfirmed}
              onChange={(e) => setAgeConfirmed(e.target.checked)}
              className="w-5 h-5 mt-0.5 accent-gold-400"
              required
              data-testid="register-age-confirm"
            />
            <label htmlFor="age-confirm" className="text-zinc-400 text-sm leading-tight">
              I confirm that I am <strong className="text-white">21 years of age or older</strong> and agree to the terms of service and privacy policy.
            </label>
          </div>
          
          <button
            type="submit"
            disabled={loading || !ageConfirmed}
            className="btn-gold w-full disabled:opacity-50"
            data-testid="register-submit"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>
        
        <p className="text-center mt-8 text-zinc-500">
          Already have an account?{' '}
          <Link to="/login" className="text-gold-400 hover:text-gold-300 transition-colors">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
